import React, { useState, useEffect, useMemo } from 'react';
import { useLMS } from '../../context/LMSContext';
import { db, supabase } from '../../lib/supabase';

const AttendanceRoster = () => {
  const {
    currentUser,
    cohorts,
    students,
    selectedCohortId,
    setSelectedCohortId,
    getCohortById,
    getEventsForCohort
  } = useLMS();

  // State
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [summaryView, setSummaryView] = useState(false);
  const [studentSummaries, setStudentSummaries] = useState({});
  const [collapsedMonths, setCollapsedMonths] = useState({});
  const [sessionFilter, setSessionFilter] = useState('all');
  const [syncing, setSyncing] = useState(false);

  const [newSession, setNewSession] = useState({
    title: '',
    sessionType: 'lecture',
    sessionDate: new Date().toISOString().split('T')[0],
    startTime: '',
    endTime: '',
    notes: ''
  });

  const activeCohortId = selectedCohortId || cohorts[0]?.id;
  const activeCohort = activeCohortId ? getCohortById(activeCohortId) : null;

  const cohortStudents = useMemo(() => {
    if (!activeCohortId) return [];
    return (students || [])
      .filter(s => (s.cohortId === activeCohortId || s.cohort_id === activeCohortId) && s.role === 'student')
      .sort((a, b) => {
        const aName = a.name || '';
        const bName = b.name || '';
        return aName.localeCompare(bName);
      });
  }, [students, activeCohortId]);

  // Load sessions when cohort changes
  useEffect(() => {
    if (activeCohortId) {
      loadSessions();
    }
  }, [activeCohortId]);

  // Load attendance records when session is selected
  useEffect(() => {
    if (selectedSession) {
      loadAttendanceRecords(selectedSession.id);
    }
  }, [selectedSession?.id]);

  const loadSessions = async () => {
    setLoading(true);
    try {
      const { data, error } = await db.getAttendanceSessions(activeCohortId);
      if (data && !error) {
        setSessions(data);
        // Auto-sync calendar events and module item live-lectures
        await syncCalendarEvents(data);
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
    setLoading(false);
  };

  // Sync calendar lecture events AND module-item live-lectures into attendance sessions
  // allSessions includes ALL sessions (even those previously deleted via dismissed_event_ids)
  const syncCalendarEvents = async (allSessions) => {
    try {
      // --- 1. Load the dismissed IDs so we never recreate deleted sessions ---
      const { data: dismissedRows } = await supabase
        .from('attendance_dismissed_events')
        .select('event_key')
        .eq('cohort_id', activeCohortId);
      const dismissedKeys = new Set((dismissedRows || []).map(r => r.event_key));

      // Build sets of already-linked sessions for dedup
      const existingLinkedIds = new Set(allSessions.map(s => s.linked_event_id).filter(Boolean));
      const existingLinkedModuleIds = new Set(allSessions.map(s => s.linked_module_item_id).filter(Boolean));
      const existingTitleDates = new Set(allSessions.map(s => `${s.title}::${s.session_date}`));

      // Helper: should we skip this event?
      const alreadyExists = (eventId, moduleItemId, title, dateStr) => {
        if (eventId && existingLinkedIds.has(eventId)) return true;
        if (moduleItemId && existingLinkedModuleIds.has(moduleItemId)) return true;
        if (existingTitleDates.has(`${title}::${dateStr}`)) return true;
        return false;
      };

      // --- 2. Cohort custom calendar events (existing behavior) ---
      const { data: calendarEvents } = await db.getCohortCustomEvents(activeCohortId);
      const lectureCalEvents = (calendarEvents || []).filter(e =>
        e.type === 'lecture' || e.type === 'live-lecture'
      );

      const unlinkedCalEvents = lectureCalEvents.filter(event => {
        const key = `cal::${event.id}`;
        if (dismissedKeys.has(key)) return false;
        return !alreadyExists(event.id, null, event.title, event.event_date);
      });

      // --- 3. Module item live-lectures (NEW: Zoom Live Lectures from Master Modules) ---
      const allCohortEvents = getEventsForCohort(activeCohortId);
      const moduleLiveLectures = allCohortEvents.filter(e =>
        e.source === 'module_item' && e.itemType === 'live-lecture'
      );

      const unlinkedModuleItems = moduleLiveLectures.filter(event => {
        const key = `mod::${event.moduleItemId}::${event.date}`;
        if (dismissedKeys.has(key)) return false;
        return !alreadyExists(null, event.moduleItemId, event.title, event.date);
      });

      if (unlinkedCalEvents.length === 0 && unlinkedModuleItems.length === 0) return;

      setSyncing(true);
      const newSessions = [];

      // Create sessions for calendar events
      for (const event of unlinkedCalEvents) {
        try {
          const { data: created } = await supabase
            .from('attendance_sessions')
            .insert({
              cohort_id: activeCohortId,
              title: event.title,
              session_type: 'lecture',
              session_date: event.event_date,
              start_time: event.start_time || null,
              end_time: event.end_time || null,
              notes: 'Auto-created from calendar event',
              linked_event_id: event.id,
              created_by: currentUser.id
            })
            .select()
            .single();
          if (created) newSessions.push(created);
        } catch (err) {
          console.error('Error syncing calendar event:', err);
        }
      }

      // Create sessions for module item live-lectures (Zoom lectures)
      for (const event of unlinkedModuleItems) {
        try {
          const { data: created } = await supabase
            .from('attendance_sessions')
            .insert({
              cohort_id: activeCohortId,
              title: event.title,
              session_type: 'lecture',
              session_date: event.date,
              start_time: event.startTime || null,
              end_time: event.endTime || null,
              notes: 'Auto-created from Zoom Live Lecture',
              linked_module_item_id: event.moduleItemId,
              created_by: currentUser.id
            })
            .select()
            .single();
          if (created) newSessions.push(created);
        } catch (err) {
          console.error('Error syncing module item live-lecture:', err);
        }
      }

      if (newSessions.length > 0) {
        setSessions(prev => [...newSessions, ...prev].sort((a, b) =>
         new Date(a.session_date) - new Date(b.session_date)
        ));
      }
      setSyncing(false);
    } catch (err) {
      console.error('Error syncing calendar events:', err);
      setSyncing(false);
    }
  };

  const loadAttendanceRecords = async (sessionId) => {
    try {
      const { data, error } = await db.getAttendanceRecords(sessionId);
      if (data && !error) {
        const recordMap = {};
        data.forEach(r => {
          recordMap[r.student_id] = r;
        });
        // If no records saved yet, leave everyone unselected (no default status)
        const hasAnyRecords = data.length > 0;
        if (!hasAnyRecords) {
          cohortStudents.forEach(student => {
            recordMap[student.id] = {
              student_id: student.id,
              status: null,
              notes: null,
              _modified: false
            };
          });
        }
        setAttendanceRecords(recordMap);
      } else {
        // No records at all - leave everyone unselected
        const recordMap = {};
        cohortStudents.forEach(student => {
          recordMap[student.id] = {
            student_id: student.id,
            status: null,
            notes: null,
            _modified: false
          };
        });
        setAttendanceRecords(recordMap);
      }
    } catch (err) {
      console.error('Error loading attendance records:', err);
      setAttendanceRecords({});
    }
  };

  const loadStudentSummaries = async () => {
    if (!activeCohortId || cohortStudents.length === 0) return;
    const summaries = {};
    for (const student of cohortStudents) {
      try {
        const { data } = await db.getStudentAttendanceSummary(student.id, activeCohortId);
        if (data) {
          summaries[student.id] = data;
        }
      } catch (err) {
        console.error('Error loading summary for', student.id, err);
      }
    }
    setStudentSummaries(summaries);
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    try {
      const { data, error } = await db.createAttendanceSession({
        cohortId: activeCohortId,
        title: newSession.title,
        sessionType: newSession.sessionType,
        sessionDate: newSession.sessionDate,
        startTime: newSession.startTime || null,
        endTime: newSession.endTime || null,
        notes: newSession.notes || null,
        createdBy: currentUser.id
      });
      if (data && !error) {
        setSessions(prev => [data, ...prev].sort((a, b) =>
          new Date(a.session_date) - new Date(b.session_date)
        ));
        setSelectedSession(data);
        setShowCreateModal(false);
        setNewSession({
          title: '',
          sessionType: 'lecture',
          sessionDate: new Date().toISOString().split('T')[0],
          startTime: '',
          endTime: '',
          notes: ''
        });
      } else {
        alert('Failed to create session: ' + (error?.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error creating session:', err);
      alert('Failed to create session');
    }
  };

  const handleUpdateSession = async (e) => {
    e.preventDefault();
    if (!editingSession) return;
    try {
      const { data, error } = await db.updateAttendanceSession(editingSession.id, {
        title: editingSession.title,
        sessionType: editingSession.session_type,
        sessionDate: editingSession.session_date,
        startTime: editingSession.start_time || null,
        endTime: editingSession.end_time || null,
        notes: editingSession.notes || null
      });
      if (data && !error) {
        setSessions(prev => prev.map(s => s.id === data.id ? data : s));
        if (selectedSession?.id === data.id) {
          setSelectedSession(data);
        }
        setShowEditModal(false);
        setEditingSession(null);
      }
    } catch (err) {
      console.error('Error updating session:', err);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!window.confirm('Delete this session and all attendance records? This cannot be undone.')) return;
    try {
      const session = sessions.find(s => s.id === sessionId);

      // If this session was auto-synced, record its event key so it won't be re-created.
      // This is best-effort: if the dismissed table doesn't exist yet, we still delete.
      try {
        if (session?.linked_event_id) {
          await supabase.from('attendance_dismissed_events').upsert({
            cohort_id: activeCohortId,
            event_key: `cal::${session.linked_event_id}`
          }, { onConflict: 'cohort_id,event_key' });
        }
        if (session?.linked_module_item_id) {
          await supabase.from('attendance_dismissed_events').upsert({
            cohort_id: activeCohortId,
            event_key: `mod::${session.linked_module_item_id}::${session.session_date}`
          }, { onConflict: 'cohort_id,event_key' });
        }
      } catch (dismissErr) {
        // If the dismissed_events table doesn't exist yet, log but continue with delete
        console.warn('Could not record dismissed event (migration may not be applied yet):', dismissErr);
      }

      // Always hard-delete the session row
      const { error } = await db.deleteAttendanceSession(sessionId);
      if (error) throw error;

      setSessions(prev => prev.filter(s => s.id !== sessionId));
      if (selectedSession?.id === sessionId) {
        setSelectedSession(null);
        setAttendanceRecords({});
      }
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const updateStudentStatus = (studentId, status) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        student_id: studentId,
        status: status,
        _modified: true
      }
    }));
  };

  const updateStudentNote = (studentId, notes) => {
    setAttendanceRecords(prev => ({
      ...prev,
      [studentId]: {
        ...(prev[studentId] || {}),
        student_id: studentId,
        notes: notes,
        _modified: true
      }
    }));
  };

  const markAllPresent = () => {
    const updated = { ...attendanceRecords };
    cohortStudents.forEach(student => {
      updated[student.id] = {
        ...(updated[student.id] || {}),
        student_id: student.id,
        status: 'present',
        _modified: true
      };
    });
    setAttendanceRecords(updated);
  };

  const markAllAbsent = () => {
    const updated = { ...attendanceRecords };
    cohortStudents.forEach(student => {
      updated[student.id] = {
        ...(updated[student.id] || {}),
        student_id: student.id,
        status: 'absent',
        _modified: true
      };
    });
    setAttendanceRecords(updated);
  };

  const saveAttendance = async () => {
    if (!selectedSession) return;

    // Warn if any students are still unselected
    const unselectedCount = cohortStudents.filter(s => !attendanceRecords[s.id]?.status).length;
    if (unselectedCount > 0) {
      const proceed = window.confirm(
        `${unselectedCount} student${unselectedCount > 1 ? 's have' : ' has'} no status selected and will not be saved. Continue?`
      );
      if (!proceed) return;
    }

    setSaving(true);
    try {
      const records = cohortStudents
        .filter(student => attendanceRecords[student.id]?.status)
        .map(student => ({
          studentId: student.id,
          status: attendanceRecords[student.id].status,
          notes: attendanceRecords[student.id]?.notes || null
        }));

      const { data, error } = await db.saveAttendanceRecords(
        selectedSession.id,
        records,
        currentUser.id
      );

      if (!error) {
        await loadAttendanceRecords(selectedSession.id);
        alert('Attendance saved successfully!');
      } else {
        alert('Failed to save attendance: ' + error.message);
      }
    } catch (err) {
      console.error('Error saving attendance:', err);
      alert('Failed to save attendance');
    }
    setSaving(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return '#4caf50';
      case 'late': return '#ff9800';
      case 'excused': return '#2196f3';
      case 'absent': return '#f44336';
      case 'left-early': return '#ab47bc';
      case 'other': return '#78909c';
      default: return '#9e9e9e';
    }
  };

  const getSessionTypeLabel = (type) => {
    switch (type) {
      case 'lecture': return '📚 Lecture';
      case 'question-session': return '❓ Q&A';
      case 'lab': return '🔬 Lab';
      case 'review': return '📝 Review';
      case 'other': return '📌 Other';
      default: return type;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Count current session stats
  const sessionStats = useMemo(() => {
    if (!selectedSession) return { present: 0, late: 0, absent: 0, excused: 0, 'left-early': 0, other: 0, unselected: 0 };
    const stats = { present: 0, late: 0, absent: 0, excused: 0, 'left-early': 0, other: 0, unselected: 0 };
    cohortStudents.forEach(student => {
      const status = attendanceRecords[student.id]?.status || null;
      if (status && stats[status] !== undefined) {
        stats[status]++;
      } else if (!status) {
        stats.unselected++;
      }
    });
    return stats;
  }, [attendanceRecords, cohortStudents, selectedSession]);

  // Group sessions by month and apply filter
  const groupedSessions = useMemo(() => {
    let filtered = sessions;
    if (sessionFilter !== 'all') {
      filtered = sessions.filter(s => s.session_type === sessionFilter);
    }

    const groups = {};
    filtered.forEach(session => {
      const [year, month] = session.session_date.split('-');
      const d = new Date(year, month - 1, 1);
      const key = `${year}-${month}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!groups[key]) {
        groups[key] = { key, label, sessions: [] };
      }
      groups[key].sessions.push(session);
    });

    return Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));
  }, [sessions, sessionFilter]);

  const toggleMonth = (monthKey) => {
    setCollapsedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  return (
    <div className="admin-page attendance-page">
      <div className="page-header">
        <div>
          <h1>📇 Attendance Roster</h1>
          <p>Track student attendance for live lectures and Q&A sessions</p>
        </div>
        <div className="header-actions">
          <select
            className="cohort-selector"
            value={activeCohortId || ''}
            onChange={(e) => {
              setSelectedCohortId(e.target.value);
              setSelectedSession(null);
              setAttendanceRecords({});
            }}
          >
            {cohorts.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            className={`btn-secondary ${summaryView ? 'active' : ''}`}
            onClick={() => {
              setSummaryView(!summaryView);
              if (!summaryView) loadStudentSummaries();
            }}
          >
            {summaryView ? '📋 Session View' : '📊 Summary View'}
          </button>
          <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
            + New Session
          </button>
        </div>
      </div>

      {syncing && (
        <div className="sync-notice">
          🔄 Syncing lecture events from calendar...
        </div>
      )}

      {!activeCohortId ? (
        <div className="empty-state">
          <span className="empty-icon">📇</span>
          <p>Select a cohort to manage attendance</p>
        </div>
      ) : summaryView ? (
        /* ============ SUMMARY VIEW ============ */
        <div className="attendance-summary">
          <h2>Attendance Summary - {activeCohort?.name}</h2>
          <p className="summary-subtitle">{sessions.length} session{sessions.length !== 1 ? 's' : ''} recorded</p>
          
          {cohortStudents.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">👥</span>
              <p>No students in this cohort</p>
            </div>
          ) : (
            <div className="summary-table-wrapper">
              <table className="data-table attendance-summary-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Total Sessions</th>
                    <th>Present</th>
                    <th>Late</th>
                    <th>Excused</th>
                    <th>Left Early</th>
                    <th>Other</th>
                    <th>Absent</th>
                    <th>Attendance Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {cohortStudents.map(student => {
                    const s = studentSummaries[student.id] || { total: 0, present: 0, late: 0, excused: 0, absent: 0, 'left-early': 0, other: 0 };
                    const rate = s.total > 0 ? Math.round(((s.present + s.late) / s.total) * 100) : 0;
                    return (
                      <tr key={student.id}>
                        <td>
                          <div className="student-name-cell">
                            <div className="student-avatar-sm">
                              {student.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </div>
                            <span>{(student.name || "").split(" ")[0]}</span>
                          </div>
                        </td>
                        <td>{s.total}</td>
                        <td><span className="status-count present">{s.present}</span></td>
                        <td><span className="status-count late">{s.late}</span></td>
                        <td><span className="status-count excused">{s.excused}</span></td>
                        <td><span className="status-count left-early">{s['left-early'] || 0}</span></td>
                        <td><span className="status-count other">{s.other || 0}</span></td>
                        <td><span className="status-count absent">{s.absent}</span></td>
                        <td>
                          <div className="attendance-rate">
                            <div className="rate-bar">
                              <div
                                className="rate-fill"
                                style={{
                                  width: `${rate}%`,
                                  backgroundColor: rate >= 90 ? '#4caf50' : rate >= 75 ? '#ff9800' : '#f44336'
                                }}
                              />
                            </div>
                            <span className="rate-text">{rate}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        /* ============ SESSION VIEW ============ */
        <div className="attendance-layout">
          {/* Sessions List */}
          <div className="sessions-panel">
            <div className="sessions-panel-header">
              <h3>Sessions</h3>
              <span className="session-count">{sessions.length}</span>
            </div>

            {/* Session type filter */}
            <div className="session-filter">
              <select
                value={sessionFilter}
                onChange={(e) => setSessionFilter(e.target.value)}
                className="session-filter-select"
              >
                <option value="all">All Types</option>
                <option value="lecture">📚 Lectures</option>
                <option value="question-session">❓ Q&A Sessions</option>
                <option value="lab">🔬 Labs</option>
                <option value="review">📝 Reviews</option>
                <option value="other">📌 Other</option>
              </select>
            </div>

            {loading ? (
              <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
            ) : groupedSessions.length === 0 ? (
              <div className="empty-state small">
                <p>{sessionFilter !== 'all' ? 'No matching sessions' : 'No sessions yet'}</p>
                {sessionFilter === 'all' && (
                  <button className="btn-primary small" onClick={() => setShowCreateModal(true)}>
                    Create First Session
                  </button>
                )}
              </div>
            ) : (
              <div className="sessions-grouped">
                {groupedSessions.map(group => {
                  const isCollapsed = collapsedMonths[group.key] || false;
                  return (
                    <div key={group.key} className="session-month-group">
                      <button
                        className="month-group-header"
                        onClick={() => toggleMonth(group.key)}
                      >
                        <span className="month-toggle">{isCollapsed ? '▶' : '▼'}</span>
                        <span className="month-label">{group.label}</span>
                        <span className="month-count">{group.sessions.length}</span>
                      </button>
                      
                      {!isCollapsed && (
                        <div className="month-sessions">
                          {group.sessions.map(session => (
                            <div
                              key={session.id}
                              className={`session-card ${selectedSession?.id === session.id ? 'active' : ''}`}
                              onClick={() => setSelectedSession(session)}
                            >
                              <div className="session-card-header">
                                <span className="session-type-badge">
                                  {getSessionTypeLabel(session.session_type)}
                                </span>
                                <div className="session-card-actions">
                                  <button
                                    className="btn-icon small"
                                    title="Edit"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingSession({ ...session });
                                      setShowEditModal(true);
                                    }}
                                  >
                                    ✏️
                                  </button>
                                  <button
                                    className="btn-icon small"
                                    title="Delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteSession(session.id);
                                    }}
                                  >
                                    🗑️
                                  </button>
                                </div>
                              </div>
                              <div className="session-card-title">{session.title}</div>
                              <div className="session-card-date">{formatShortDate(session.session_date)}</div>
                              {session.start_time && (
                                <div className="session-card-time">
                                  {session.start_time}{session.end_time ? ` - ${session.end_time}` : ''}
                                </div>
                              )}
                              {session.linked_event_id && (
                                <div className="session-card-synced" title="Auto-synced from calendar">
                                  🔗 Calendar
                                </div>
                              )}
                              {session.linked_module_item_id && !session.linked_event_id && (
                                <div className="session-card-synced" title="Auto-synced from Zoom Live Lecture">
                                  📡 Zoom Lecture
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Attendance Taking Panel */}
          <div className="roster-panel">
            {!selectedSession ? (
              <div className="empty-state">
                <span className="empty-icon">📋</span>
                <p>Select a session to take attendance</p>
              </div>
            ) : (
              <>
                <div className="roster-header">
                  <div>
                    <h2>{selectedSession.title}</h2>
                    <p>{formatDate(selectedSession.session_date)}
                      {selectedSession.start_time && ` | ${selectedSession.start_time}`}
                      {selectedSession.end_time && ` - ${selectedSession.end_time}`}
                    </p>
                  </div>
                  <div className="roster-actions">
                    <button className="btn-secondary small" onClick={markAllPresent}>
                      ✅ All Present
                    </button>
                    <button className="btn-secondary small" onClick={markAllAbsent}>
                      ❌ All Absent
                    </button>
                    <button className="btn-primary" onClick={saveAttendance} disabled={saving}>
                      {saving ? 'Saving...' : '💾 Save Attendance'}
                    </button>
                  </div>
                </div>

                {/* Stats bar */}
                <div className="attendance-stats-bar">
                  <span className="stat-chip present">✅ {sessionStats.present} Present</span>
                  <span className="stat-chip late">⏰ {sessionStats.late} Late</span>
                  <span className="stat-chip excused">📝 {sessionStats.excused} Excused</span>
                  <span className="stat-chip left-early">🚪 {sessionStats['left-early']} Left Early</span>
                  <span className="stat-chip other">📌 {sessionStats.other} Other</span>
                  <span className="stat-chip absent">❌ {sessionStats.absent} Absent</span>
                  {sessionStats.unselected > 0 && (
                    <span className="stat-chip unselected">⬜ {sessionStats.unselected} Unselected</span>
                  )}
                  <span className="stat-chip total">👥 {cohortStudents.length} Total</span>
                </div>

                {/* Student Roster */}
                {cohortStudents.length === 0 ? (
                  <div className="empty-state">
                    <span className="empty-icon">👥</span>
                    <p>No students in this cohort</p>
                  </div>
                ) : (
                  <div className="roster-list">
                    {cohortStudents.map(student => {
                      const record = attendanceRecords[student.id];
                      const status = record?.status || null;
                      
                      return (
                        <div key={student.id} className={`roster-row ${status || 'unselected'}`}>
                          <div className="roster-student">
                            <div className="student-avatar-sm">
                              {student.name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </div>
                            <div className="student-info">
                              <span className="student-name">{(student.name || "").split(" ")[0]}</span>
                              <span className="student-email">{student.email}</span>
                            </div>
                          </div>
                          
                          <div className="roster-status-buttons">
                            {['present', 'late', 'excused', 'left-early', 'other', 'absent'].map(s => (
                              <button
                                key={s}
                                className={`status-btn ${s} ${status === s ? 'active' : ''}`}
                                onClick={() => updateStudentStatus(student.id, s)}
                                style={status === s ? { backgroundColor: getStatusColor(s), color: '#fff' } : {}}
                              >
                                {s === 'present' ? '✅' : s === 'late' ? '⏰' : s === 'excused' ? '📝' : s === 'left-early' ? '🚪' : s === 'other' ? '📌' : '❌'}
                                <span className="status-label">{s === 'left-early' ? 'Left Early' : s.charAt(0).toUpperCase() + s.slice(1)}</span>
                              </button>
                            ))}
                          </div>
                          
                          <div className="roster-note">
                            <input
                              type="text"
                              placeholder="Note..."
                              value={record?.notes || ''}
                              onChange={(e) => updateStudentNote(student.id, e.target.value)}
                              className="note-input"
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Attendance Session</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>×</button>
            </div>
            <form onSubmit={handleCreateSession}>
              <div className="form-group">
                <label>Session Title</label>
                <input
                  type="text"
                  placeholder="e.g., Week 3 - Monday Lecture"
                  value={newSession.title}
                  onChange={(e) => setNewSession({ ...newSession, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Session Type</label>
                <select
                  value={newSession.sessionType}
                  onChange={(e) => setNewSession({ ...newSession, sessionType: e.target.value })}
                >
                  <option value="lecture">📚 Lecture</option>
                  <option value="question-session">❓ Q&A Session</option>
                  <option value="lab">🔬 Lab</option>
                  <option value="review">📝 Review</option>
                  <option value="other">📌 Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={newSession.sessionDate}
                  onChange={(e) => setNewSession({ ...newSession, sessionDate: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={newSession.startTime}
                    onChange={(e) => setNewSession({ ...newSession, startTime: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={newSession.endTime}
                    onChange={(e) => setNewSession({ ...newSession, endTime: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  rows={3}
                  placeholder="Any notes about this session..."
                  value={newSession.notes}
                  onChange={(e) => setNewSession({ ...newSession, notes: e.target.value })}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Session Modal */}
      {showEditModal && editingSession && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Session</h3>
              <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <form onSubmit={handleUpdateSession}>
              <div className="form-group">
                <label>Session Title</label>
                <input
                  type="text"
                  value={editingSession.title}
                  onChange={(e) => setEditingSession({ ...editingSession, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Session Type</label>
                <select
                  value={editingSession.session_type}
                  onChange={(e) => setEditingSession({ ...editingSession, session_type: e.target.value })}
                >
                  <option value="lecture">📚 Lecture</option>
                  <option value="question-session">❓ Q&A Session</option>
                  <option value="lab">🔬 Lab</option>
                  <option value="review">📝 Review</option>
                  <option value="other">📌 Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date</label>
                <input
                  type="date"
                  value={editingSession.session_date}
                  onChange={(e) => setEditingSession({ ...editingSession, session_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Start Time</label>
                  <input
                    type="time"
                    value={editingSession.start_time || ''}
                    onChange={(e) => setEditingSession({ ...editingSession, start_time: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>End Time</label>
                  <input
                    type="time"
                    value={editingSession.end_time || ''}
                    onChange={(e) => setEditingSession({ ...editingSession, end_time: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  rows={3}
                  value={editingSession.notes || ''}
                  onChange={(e) => setEditingSession({ ...editingSession, notes: e.target.value })}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceRoster;
